/***********************************************
ICEMEDX - Developed By ICEMEDX, Inc.
Copyright 2018 ICEMEDX, Inc.
***********************************************/
import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, Subject } from 'rxjs';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { tap, map } from 'rxjs/operators';
import { XmlparseService } from '../xml/xmlparse.service';
import { AlertService } from "../../services/alert/alert.service";
import { IceTypes } from '../../models/icetypes';
import { Iceutility } from 'projects/icemedx-lib/src/services/iceutility';
import { LoginService } from 'projects/icemedx-lib/src/login/login.service';
import { orderobject } from '../../models/domain/order';
import { licenseobject } from '../../models/domain/license';
import { basecode } from '../../models/ehr/subs/basecode';
import { externaldata } from '../../models/ehr/externaldata';
import { country } from '../../models/common/country';
import { user } from '../../models/common/user';

declare var require: any;

export interface IConsentListItem {
  key: string;
  external_data_key: string;
  full_name: string;
  login_user_name: string;
  date_of_consent: string;
  title_of_terms: string;
  status: string;
  agree: boolean;
}

export class ConsentListObject implements IConsentListItem {
  key: string = "";
  external_data_key: string = "";
  full_name: string = "";
  login_user_name: string = "";
  date_of_consent: string = "";
  title_of_terms: string = "";
  status: string = "";
  agree: boolean = false;

  constructor(key: string, extkey: string, memberid: string, name: string, title: string, stat: string) {
    this.key = key;
    this.external_data_key = extkey;
    this.login_user_name = memberid;
    this.full_name = name;
    this.title_of_terms = title;
    this.status = stat;

    this.date_of_consent = Iceutility.getDateTextEx(Iceutility.GetLocalDate(), null, true);
  }
}

export interface IMemberListItem {
  login_user_name: string;
  first_name: string;
  last_name: string;
  middle: string;
  prefix: string;
  suffix: string;
}

export class MemberListObject implements IMemberListItem {
  login_user_name: string = "";
  first_name: string = "";
  last_name: string = "";
  middle: string = "";
  prefix: string = "";
  suffix: string = "";

  constructor(uid: string, fname: string,
    lname: string,
    middle: string,
    pre: string,
    suf: string) {
    this.login_user_name = uid;
    this.first_name = fname;
    this.last_name = lname;
    this.middle = middle;
    this.prefix = pre;
    this.suffix = suf;
  }
}

@Injectable()
export class DomainService extends XmlparseService {
  static mappedProviders: object = {};
  static mappedMembers: object = {};
  private fileLoadSbj = new Subject<any>();
  private dataSubject = new BehaviorSubject(null);
  dataSubject$ = this.dataSubject.asObservable();

  constructor(private http: HttpClient,
    private alertSvc: AlertService, private loginSvc: LoginService) {
    super(alertSvc, loginSvc);
  }

  // To do: move this service call to the message.service file near loadMessageFile
  public loadMessageFileCallback(key: string, extkey: string, src_type: number, comp: any, callback: Function, errorCallback: Function = null) {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";
    if (comp == null || callback == null) throw "Callback function not yet set.";
    var bloburl = null;

    try {
      let url = envs[currentEnv].icemedx_api + "/members/default.aspx?domain=&data=messagefileattachment&domaintype=&patientdomain=" + "&key=" + key + "&subkeys=extkey:" + extkey + ";srctype:" + src_type.toString();

      var req = new XMLHttpRequest();

      req.open("GET", url, true);
      req.responseType = "blob";

      let userInfo = this.loginSvc.GetSessionUserInfo();
      var token = this.loginSvc.GetAccessToken(userInfo);
      if (token != null) {
        req.setRequestHeader("Authorization", 'Bearer ' + token);

        req.onload = function (event) {
          if (Iceutility.toLong(event["currentTarget"]["status"]) == 200) {
            callback(req.response, comp);
          } else if (errorCallback) {
            callback(req.response, comp);
          }
        };

        req.send();
      }
    }
    catch (e) {
      console.log(e.message);
    }
  }

  getHttp(): HttpClient {
    return this.http;
  }

  getLoginService(): LoginService {
    return this.loginSvc;
  }

  public getCountries(): Observable<country[]> {
    return this.http.get<country[]>("https://countriesnow.space/api/v0.1/countries/iso")
      .pipe(map((response: any) => response.data));
  }

  public getProviders(): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/members/default.aspx?domain=&data=networkofproviders&providerdomain=&domaintype=&patientdomain=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseProviders(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getVendors(): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/members/default.aspx?domain=&data=networkofvendors&providerdomain=&domaintype=&patientdomain=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseVendors(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getPharmacies(): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/members/default.aspx?domain=&data=networkofpharmacies&providerdomain=&domaintype=&patientdomain=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parsePharmacies(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getPayers(): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/members/default.aspx?domain=&data=networkofpayers&providerdomain=&domaintype=&patientdomain=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parsePayers(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getPatients(): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/members/default.aspx?domain=&data=networkofpatients&providerdomain=&domaintype=&patientdomain=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["PATIENT_INDEX"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getSubscribers(): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/members/default.aspx?domain=&data=networkofsubscribers&providerdomain=&domaintype=&patientdomain=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["SUBSCRIBER_INDEX"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  private parseProviders(prvData: any) {
    let results = this.geticexdocumentobjects(prvData, ["PROVIDER_INDEX"]);
    if (results) {
      results = results.map(pro => { pro["name"] = pro.getdisplayname(pro.account_type == IceTypes.ACCOUNT_TYPE.actFacility); return pro; });
    }
    return results;
  }

  private parseVendors(venData: any) {
    let results = this.geticexdocumentobjects(venData, ["VENDOR_INDEX"]);
    if (results) {
      results = results.map(ven => { ven["name"] = ven.getdisplayname(true); return ven; });
    }
    return results;
  }

  private parsePharmacies(phrmData: any) {
    let results = this.geticexdocumentobjects(phrmData, ["PHARMACY_INDEX"]);
    if (results) {
      results = results.map(phrm => { phrm["name"] = phrm.getdisplayname(true); return phrm; });
    }
    return results;
  }

  private parsePayers(pyrData: any) {
    let results = this.geticexdocumentobjects(pyrData, ["PAYER_INDEX"]);
    if (results) {
      results = results.map(pyr => { pyr["name"] = pyr.getdisplayname(true); return pyr; });
    }
    return results;
  }

  public getEHRRootUrl() {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain server is not configured.";

    return envs[currentEnv].ehr_server + "/";
  }

  public getEHRUrl() {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain server is not configured.";

    return envs[currentEnv].ehr_server + "/" + envs[currentEnv].ehr_path;
  }
  public getEHR_PHRUrl() {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain server is not configured.";

    return envs[currentEnv].ehr_server + "/PHR/";
  }
  public getHHCRootUrl() {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain server is not configured.";

    return envs[currentEnv].hhc_server + "/";
  }

  public getPLTRootUrl() {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain server is not configured.";

    return envs[currentEnv].platform_server + "/";
  }

  public getMembersList(act: string, acttype: IceTypes.ACCOUNT_TYPE): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/members/default.aspx?domain=" + act + "&data=privatenetwork" + "&domaintype=" + acttype.toString() + "&patientdomain=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["USER"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getPatientProviderMembersList(act: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/members/default.aspx?domain=" + act + "&data=networkofpatientproviders" + "&domaintype=" + IceTypes.ACCOUNT_TYPE.actPatient.toString() + "&patientdomain=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["USER"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getNetworkOfMembers(): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/members/default.aspx?domain=&data=publicnetwork&domaintype=&patientdomain=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["USER"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public mapMembers(members: string) {
    try {
      let currentEnv: any = require("../../../../../env.json").currentEnv;
      let envs: any = require("../../../../../env.json").environments;
      if (!envs || !currentEnv) throw "Login service is not configured.";
      if (!Iceutility.isdefined(members) || members.length == 0) throw "Invalid members parameter.";

      let url = envs[currentEnv].icemedx_api + "/members/default.aspx?domain=&data=mapmembers&key=&subkeys=messagebox:;status:;";
      return this.http.post(url, members, { responseType: 'text' }).pipe(
        tap(
          data => this.parseLogReport(url, data),
          error => this.parseLogReport(url, error)
        )).map(
          response => {
            let users = this.geticexdocumentobjects(response, ["USER"]);
            if (users == null || users.length == 0) return [];
            for (var iMember = 0; iMember < users.length; iMember++) {
              let member = users[iMember];
              if (Iceutility.isdefined(DomainService.mappedMembers[member["login_user_name"]])) {
                continue;
              }

              DomainService.mappedMembers[member["login_user_name"].toLowerCase()] = member;
            }
            return users;
          }
        );
    } catch (e) {
      console.error(e.message);
    }
  }

  public getUserProfileUrl(): string {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";
    return envs[currentEnv].login_server + "/Home/UserProfile";
  }

  public searchNetworkNow(keyword: string) {
    try {
      let currentEnv: any = require("../../../../../env.json").currentEnv;
      let envs: any = require("../../../../../env.json").environments;
      if (!envs || !currentEnv) throw "Login service is not configured.";
      if (!Iceutility.isdefined(keyword)) throw "Invalid keyword parameter.";

      let url = envs[currentEnv].icemedx_api + "/members/default.aspx?domain=&data=searchnetworknowehr&key=&subkeys=keyword:" + keyword + ";";
      return this.http.get(url, { responseType: 'text' }).pipe(
        map(
          response => this.geticexdocumentobjects(response, ["USER_TRANS_ACCOUNT"])
        ),
        tap(
          data => this.parseLogReport(url, data),
          error => this.parseLogReport(url, error)
        ));
    } catch (e) {
      console.error(e.message);
    }
  }

  public insertMemberPhoto(memberid: string, extkey: string, file: File): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Login service is not configured.";
    let url = envs[currentEnv].icemedx_api + "/members/default.aspx?domain=&data=memberphoto&acttype=&key=&subkeys=extkey:" + extkey + ";srctype:";
    const formData = new FormData();
    formData.append('image', file);
    return this.http.post(url, formData, { responseType: 'text' }).pipe(
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error)
      )).map(response => this.geticexdocumentobjects(response, ["EXTERNAL_DATA"]));
  }

  public removeMemberPhoto(memberid: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Login service is not configured.";
    let url = envs[currentEnv].icemedx_api + "/members/default.aspx?domain=&data=removememberphoto&acttype=&key=&subkeys=extkey:;srctype:";
    return this.http.post(url, "{}", { responseType: 'text' }).pipe(
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error)
      )).map(response => this.geticexdocumentobjects(response, ["EXTERNAL_DATA"]));
  }

  public loadMemberPhoto(memberid: string, extkey: string, comp: any, callback: Function, errorCallback: Function = null) {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";
    if (memberid == null || memberid.length == 0) throw "MemberID is required for Profile picture.";
    if (comp == null || callback == null) throw "Callback function not yet set.";
    var bloburl = null;

    try {
      let url = envs[currentEnv].icemedx_api + "/members/default.aspx?domain=&data=memberphoto&acttype=&key=&subkeys=memberid:" + memberid + ";extkey:" + extkey + ";srctype:";
      var req = new XMLHttpRequest();

      req.open("GET", url, true);
      req.responseType = "blob";

      let userInfo = this.loginSvc.GetSessionUserInfo();
      var token = this.loginSvc.GetAccessToken(userInfo);
      if (token != null) {
        req.setRequestHeader("Authorization", 'Bearer ' + token);

        req.onload = function (event) {
          if (Iceutility.toLong(event["currentTarget"]["status"]) == 200) {
            callback(req.response, comp);
          } else if (errorCallback) {
            callback(req.response, comp);
          }
        };

        req.send();
      }
    }
    catch (e) {
      console.log(e.message);
    }
  }


  public getDefaultConsents(): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/members/default.aspx?domain=&data=consents&domaintype=&patientdomain=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["CONSENT_REFERENCE"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getProvider(domain: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";

    if (domain == null || domain.length == 0) throw "Invalid domain parameter.";

    let url = envs[currentEnv].icemedx_api + "/providers/default.aspx?domain=" + domain + "&data=profile";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["FACILITY", "PROVIDER", "CONTACT", "ENTITY", "ADDRESS", "IDENTIFICATION"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getVendor(domain: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";

    if (domain == null || domain.length == 0) throw "Invalid domain parameter.";

    let url = envs[currentEnv].icemedx_api + "/vendors/default.aspx?domain=" + domain + "&data=profile";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["VENDOR"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getPharmacy(domain: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";

    if (domain == null || domain.length == 0) throw "Invalid domain parameter.";

    let url = envs[currentEnv].icemedx_api + "/pharmacies/default.aspx?domain=" + domain + "&data=profile";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["PHARMACY"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getPayer(domain: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";

    if (domain == null || domain.length == 0) throw "Invalid domain parameter.";

    let url = envs[currentEnv].icemedx_api + "/payers/default.aspx?domain=" + domain + "&data=profile";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["PAYER"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getHIEProvider(domain: string, domaintype: IceTypes.ACCOUNT_TYPE, hiedomain: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";

    if (domain == null || domain.length == 0) throw "Invalid domain parameter.";

    let url = envs[currentEnv].icemedx_api + "/members/default.aspx?domain=" + domain + "&data=hiegetprovider&domaintype=" + domaintype.toString() + "&key=&subkeys=domainact:" + hiedomain;
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["MESSAGE"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getHIEOrganization(domain: string, domaintype: IceTypes.ACCOUNT_TYPE): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";

    if (domain == null || domain.length == 0) throw "Invalid domain parameter.";

    let url = envs[currentEnv].icemedx_api + "/members/default.aspx?domain=" + domain + "&data=hiegetorganizaton&domaintype=" + domaintype.toString() + "&key=&subkeys=domainact:";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["MESSAGE"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  /**
  * Gets the provider. This method is specifically used for Appointment list to handle async nature of call
  * @param domain
  * @returns Provider object
  */
  public getProviderName(domain: string): Observable<any> {
    let provider = {};
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";

    if (domain == null || domain.length == 0) throw "Invalid domain parameter.";

    let url = envs[currentEnv].icemedx_api + "/providers/default.aspx?domain=" + domain + "&data=profile";

    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => {
          let providers = this.geticexdocumentobjects(response, ["FACILITY", "PROVIDER", "CONTACT", "ENTITY", "ADDRESS", "IDENTIFICATION"]);
          if (providers && providers.length > 0) {
            let provider = providers[0],
              ent = provider.getsubtreedata("ENTS", "entity_key", provider.entity_key);

            if (ent != null) {
              let firstName = ent["first_name"],
                lastName = ent["last_name"];

              provider._providerName = !Iceutility.isStringUndefinedNullOrEmpty(firstName) || !Iceutility.isStringUndefinedNullOrEmpty(lastName) ? firstName + " " + lastName : ent["organization_name"];
              DomainService.mappedProviders[provider.trans_account.toLowerCase()] = provider._providerName;
            }
          }
          return providers;
        }
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getProviderNames(providerDomains: string[]): Observable<any> {
    let promises = [];

    for (let provider of providerDomains) {
      if (!Iceutility.isStringUndefinedNullOrEmpty(provider.trim())) {
        let mappedProvider = DomainService.mappedProviders[provider.trim().toLowerCase()];

        if (Iceutility.isdefined(mappedProvider)) {
          promises.push(of(mappedProvider));
        } else {
          promises.push(this.getProviderName(provider.trim()));
        }
      }
    }

    return forkJoin(promises);
  }

  public getPatient(domain: string, providerdomain: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";

    if (domain == null || domain.length == 0) throw "Invalid domain parameter.";
    if (providerdomain == null || providerdomain.length == 0) providerdomain = "";

    let url = envs[currentEnv].icemedx_api + "/patients/default.aspx?domain=" + domain + "&data=demo" + "&providerdomain=" + (providerdomain == null || providerdomain.length == 0 ? '' : providerdomain);
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["PATIENT", "CONTACT", "ENTITY", "ADDRESS", "IDENTIFICATION"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getPatientsByKeyword(searchDomainType: IceTypes.ACCOUNT_TYPE, searchDomain: string, keywordType: IceTypes.PatientSearchType, keyword: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";

    if (searchDomain == null || searchDomain.length == 0) throw "Invalid search domain parameter.";
    if (keyword == null || keyword.length == 0) throw "Invalid keyword parameter.";

    let url = envs[currentEnv].icemedx_api + "/patients/default.aspx?domain=&data=&providerdomain=" + searchDomain + "&key=&subkeys=keywordtype|" + keywordType + ";domaintype|" + searchDomainType.toString() + ";keywords|" + keyword + ";";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["PATIENT_INDEX"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getSubscribersByKeyword(searchDomainType: IceTypes.ACCOUNT_TYPE, searchDomain: string, keywordType: IceTypes.PatientSearchType, keyword: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";

    if (searchDomain == null || searchDomain.length == 0) throw "Invalid search domain parameter.";
    if (keyword == null || keyword.length == 0) throw "Invalid keyword parameter.";

    let url = envs[currentEnv].icemedx_api + "/subscribers/default.aspx?domain=&data=subscribersearch&key=&subkeys=keywordtype|" + keywordType + ";domaintype|" + searchDomainType.toString() + ";domainact|" + searchDomain +  ";keywords|" + keyword + ";";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["SUBSCRIBER_INDEX"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getSubscriber(domain: string, providerdomain: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";

    if (domain == null || domain.length == 0) throw "Invalid domain parameter.";
    if (providerdomain == null || providerdomain.length == 0) providerdomain = "";

    let url = envs[currentEnv].icemedx_api + "/subscribers/default.aspx?domain=" + domain + "&data=profile" + "&providerdomain=" + (providerdomain == null || providerdomain.length == 0 ? '' : providerdomain);
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["SUBSCRIBER"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  //To do replace pacttype with => networkDomain?: string, networkDomainType?: IceTypes.ACCOUNT_TYPE
  getDocuments(api: string, domain: string, pacttype: IceTypes.ACCOUNT_TYPE, srctype: IceTypes.SourceType): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (api == null || api.length == 0) throw "Service is not configured.";
    if (!envs || !currentEnv) throw api + " service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/" + api +"?domain=" + domain + "&data=documents" + "&key=&subkeys=parent_src_type:" + srctype.toString() + ";";
    return this.getHttp().get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["EXTERNAL_DATA"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  downloadDocument(api: string, domain: string, key: string, srcType: IceTypes.SourceType) {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Payer service is not configured.";

    let blobUrl = null,
      url = envs[currentEnv].icemedx_api + "/" + api +"/default.aspx?domain=" + domain + "&data=file&key=" + key + "&subkeys=&type=" + srcType,
      userInfo = this.loginSvc.GetSessionUserInfo();

    if (this.loginSvc.IsSignedIn(userInfo)) {
      var auth = this.loginSvc.GetAccessToken(userInfo);
      var req = new XMLHttpRequest();

      req.open("GET", url, true);
      req.responseType = "blob";
      req.setRequestHeader("Authorization", "Bearer " + auth);

      let domSvc: DomainService = this;
      req.onload = function (event) {
        var blob = req.response;
        var newBlob = new Blob([blob], { type: blob.type });
        blobUrl = window.URL.createObjectURL(newBlob);
        window.open(blobUrl);
        setTimeout(function () {
          window.URL.revokeObjectURL(blobUrl);
        }, 100);
      };
      req.onreadystatechange = function () {
        if (req.readyState === 4) {
          if (req.status != 200) {
            domSvc.logMessage(IceTypes.LOGTYPE.ltError, req.statusText);
          }
        }
      }
      req.send();
    }
  }

  public insertDocument(api: string, domain: string, doc: externaldata, networkDomain?: string, networkDomainType?: IceTypes.ACCOUNT_TYPE): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Login service is not configured.";
    if (doc == null) throw api + " service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/" + api + "/default.aspx?domain=" + domain + "&data=document";
    if (api == "patients" && networkDomain != null)
      url += "&providerdomain=" + networkDomain;

    url += "&key=";

    if (api == "patients" && networkDomain != null)
      url += "&subkeys=domaintype:" + networkDomainType.toString();

    let tblname = doc.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(doc) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error)
      )).map(response => this.geticexdocumentobjects(response, ["EXTERNAL_DATA"]));
  }

  public updateDocument(api: string, domain: string, doc: externaldata, networkDomain?: string, networkDomainType?: IceTypes.ACCOUNT_TYPE): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Login service is not configured.";
    if (doc == null) throw api + " service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/" + api + "/default.aspx?domain=" + domain + "&data=updatedocument&key=" + doc.external_data_key;
    if (api == "patients" && networkDomain != null)
      url += "&providerdomain=" + networkDomain;

    url += "&key=";

    if (api == "patients" && networkDomain != null)
      url += "&subkeys=domaintype:" + networkDomainType.toString();

    let tblname = doc.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(doc) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error)
      )).map(response => this.geticexdocumentobjects(response, ["EXTERNAL_DATA"]));
  }

  public insertFile(api: string, domain: string, extkey: string, srctyp: IceTypes.SourceType, file: File, networkDomain?: string, networkDomainType?: IceTypes.ACCOUNT_TYPE): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Login service is not configured.";
    let url = envs[currentEnv].icemedx_api + "/" + api + "/default.aspx?domain=" + domain + "&data=file&key=" + extkey + "&subkeys=parent_src_type:" + srctyp.toString();
    if (api == "patients" && networkDomain != null && networkDomainType != null)
      url += (";providerdomain:" + networkDomain + ";domaintype:" + networkDomainType.toString());

    const formData = new FormData();
    formData.append('image', file);
    return this.getHttp().post(url, formData, { responseType: 'text' }).pipe(
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error)
      )).map(response => this.geticexdocumentobjects(response, ["EXTERNAL_DATA"]));
  }

  public updateFile(api: string, domain: string, extkey: string, srctyp: IceTypes.SourceType, file: File, networkDomain?: string, networkDomainType?: IceTypes.ACCOUNT_TYPE): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Login service is not configured.";
    let url = envs[currentEnv].icemedx_api + "/" + api + "/default.aspx?domain=" + domain + "&data=updatefile&key=" + extkey + "&subkeys=parent_src_type:" + srctyp.toString();
    if (api == "patients" && networkDomain != null && networkDomainType != null)
      url += (";providerdomain:" + networkDomain + ";domaintype:" + networkDomainType.toString());
    const formData = new FormData();
    formData.append('image', file);
    return this.getHttp().post(url, formData, { responseType: 'text' }).pipe(
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error)
      )).map(response => this.geticexdocumentobjects(response, ["EXTERNAL_DATA"]));
  }

  public deleteDocument(api: string, domain: string, extkey: string, srctyp: IceTypes.SourceType): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Login service is not configured.";
    let url = envs[currentEnv].icemedx_api + "/" + api + "/default.aspx?domain=" + domain + "&data=deletedocument&key=" + extkey + "&subkeys=parent_src_type:" + srctyp.toString();
    let data = "{}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error)
      )).map(response => this.geticexdocumentobjects(response, ["EXTERNAL_DATA"]));
  }

  public loadConsentFile(memberID: string, key: string, extkey: string, srctype: IceTypes.SourceType, consentFileLoadedCallback = null) {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";


    var bloburl = null;

    try {
      let url = envs[currentEnv].icemedx_api + "/members/default.aspx?domain=&data=consentfile&domaintype=&patientdomain=" + "&key=" + key + "&subkeys=extkey:" + extkey + ";srctype:" + srctype.toString() + ";member:" + memberID;
      var req = new XMLHttpRequest();

      req.open("GET", url, true);
      req.responseType = "blob";

      let userInfo = this.loginSvc.GetSessionUserInfo();
      var token = this.loginSvc.GetAccessToken(userInfo);
      if (token != null) {
        req.setRequestHeader("Authorization", 'Bearer ' + token);

        req.onload = function (event) {
          var blob = req.response;
          var newBlob = new Blob([blob], { type: blob.type });
          bloburl = window.URL.createObjectURL(newBlob);
          window.open(bloburl);
          setTimeout(function () {
            window.URL.revokeObjectURL(bloburl);
            if (consentFileLoadedCallback !== null) {
              consentFileLoadedCallback();
            }
          }, 100);
        };
        req.onreadystatechange = function (oEvent) {
          if (req.readyState === 4) {
            if (req.status != 200) {
              console.log(req.statusText);
              if (consentFileLoadedCallback !== null) {
                consentFileLoadedCallback();
              }
            }
          }
        };

        req.send();
      }
    }
    catch (e) {
      console.log(e.message);
    }
  }

  public getLicences(): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/members/default.aspx?domain=&data=licenses&domaintype=&patientdomain=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["LICENSE"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getSubscriptions(domaintype: IceTypes.ACCOUNT_TYPE, domain: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/members/default.aspx?domain=" + domain + "&data=subscriptions&domaintype=" + domaintype.toString() + "&patientdomain=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["SUBSCRIPTION"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getSureScriptsConnectivityStatus(): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/members/default.aspx?domain=&data=surescriptsconnectivity&domaintype=&patientdomain=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseLogReport(url, response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }
  
  public processPayment(ord: orderobject): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";
    let url = envs[currentEnv].icemedx_api + "/members/default.aspx?domain=&data=processpayment&acttype=&key=&subkeys=extkey:;srctype:";
    let tblname = ord.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(ord) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error)
      )).map(response => this.geticexdocumentobjects(response, ["ORDER"]));
  }
  
  supportedLicenseCountry(licObj: licenseobject, countries: basecode[]): boolean {
    if (countries == null || countries.length == 0)
      return false;

    if (licObj.COUNTRIES == null || licObj.COUNTRIES.length == 0)
      return false;

    for (let icntry = 0; icntry < licObj.COUNTRIES.length; icntry++) {
      let cntry = licObj.COUNTRIES[icntry];
      if (cntry == null) continue;

      if (countries.filter(c => c.code == cntry.code).length > 0)
        return true;
    }
    return false;
  }

  supportedLicenseCurrency(licObj: licenseobject, currencies: basecode[]): boolean {
    if (currencies == null || currencies.length == 0)
      return false;

    if (licObj.CURRENCIES == null || licObj.CURRENCIES.length == 0)
      return false;

    for (let icur = 0; icur < licObj.CURRENCIES.length; icur++) {
      let cur = licObj.CURRENCIES[icur];
      if (cur == null) continue;

      if (currencies.filter(c => c.code == cur.code).length > 0)
        return true;
    }
    return false;
  }

  public broadcastData(data) {
    this.dataSubject.next(data);
  }

  getOptions(api: string, domain: string, domaintype: IceTypes.ACCOUNT_TYPE): Observable<any> {
    if (api == null || api.length == 0) of(false);

    let currentEnv: any = require("../../../../../env.json").currentEnv,
      envs: any = require("../../../../../env.json").environments;

    if (!envs || !currentEnv) throw "Domain service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/" + api + "?domain=" + domain + "&data=options&patientdomain=&key=&subkeys=&acttype=" + domaintype.toString();

    if (url == null)
      return of(false);

    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["OPTION"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public static getDomainTypes(): any[] {
    let list = [
      { value: IceTypes.ACCOUNT_TYPE.actFacility, name: DomainService.getDomainType(IceTypes.ACCOUNT_TYPE.actFacility) },
      { value: IceTypes.ACCOUNT_TYPE.actProvider, name: DomainService.getDomainType(IceTypes.ACCOUNT_TYPE.actProvider) },
      { value: IceTypes.ACCOUNT_TYPE.actPharmacy, name: DomainService.getDomainType(IceTypes.ACCOUNT_TYPE.actPharmacy) },
      { value: IceTypes.ACCOUNT_TYPE.actPayer, name: DomainService.getDomainType(IceTypes.ACCOUNT_TYPE.actPayer) },
      { value: IceTypes.ACCOUNT_TYPE.actVendor, name: DomainService.getDomainType(IceTypes.ACCOUNT_TYPE.actVendor) },
      { value: IceTypes.ACCOUNT_TYPE.actPatient, name: DomainService.getDomainType(IceTypes.ACCOUNT_TYPE.actPatient) },
      { value: IceTypes.ACCOUNT_TYPE.actSubscriber, name: DomainService.getDomainType(IceTypes.ACCOUNT_TYPE.actSubscriber) },
    ];
    list.sort(function (a, b) { return a.name.localeCompare(b.name) });
    return list;
  }

  public static getDomainType(type: number): string {
    switch (type) {
      case IceTypes.ACCOUNT_TYPE.actFacility:
        return "Health Facility or Organization";
      case IceTypes.ACCOUNT_TYPE.actProvider:
        return "Provider";
      case IceTypes.ACCOUNT_TYPE.actPharmacy:
        return "Pharmacy";
      case IceTypes.ACCOUNT_TYPE.actPayer:
        return "Payer (Insurance Company or Government)";
      case IceTypes.ACCOUNT_TYPE.actVendor:
        return "Vendor";
      case IceTypes.ACCOUNT_TYPE.actPatient:
        return "Patient";
      case IceTypes.ACCOUNT_TYPE.actSubscriber:
        return "Subscriber";
    }
  }

  public static getNoteStatuses(): any[] {
    let list = [
      { value: IceTypes.notestatus.statusInitialDraft, name: DomainService.getNoteStatus(IceTypes.notestatus.statusInitialDraft) },
      { value: IceTypes.notestatus.statusInProgress, name: DomainService.getNoteStatus(IceTypes.notestatus.statusInProgress) },
      { value: IceTypes.notestatus.statusComplete, name: DomainService.getNoteStatus(IceTypes.notestatus.statusComplete) },
      { value: IceTypes.notestatus.statusCompleteRequired, name: DomainService.getNoteStatus(IceTypes.notestatus.statusCompleteRequired) },
      { value: IceTypes.notestatus.statusPatientCompleteRequired, name: DomainService.getNoteStatus(IceTypes.notestatus.statusPatientCompleteRequired) },
    ];
    list.sort(function (a, b) { return a.name.localeCompare(b.name) });
    return list;
  }

  public static getNoteStatus(status: number): string {
    switch (status) {
      case IceTypes.notestatus.statusInitialDraft:
        return "Initial Draft";
      case IceTypes.notestatus.statusInProgress:
        return "In progress";
      case IceTypes.notestatus.statusComplete:
        return "Complete";
      case IceTypes.notestatus.statusCompleteRequired:
        return "Complete Required";
      case IceTypes.notestatus.statusPatientCompleteRequired:
        return "Patient or Subscriber Completion Required";
    }
    return "Initial Draft"
  }
}
